import { DataLayerOptional, PageContentType, UserDataLayer } from './types'
import { resolveLocalDevSfccOrigin } from '~/utils/local-dev-sfcc'

import { NuxtApp } from '#app'

export type FirstLoadPage = {
  title: string
  fullPath: string
  path: string
  locale: Locales
}

export function firstLoadEvent(
  userStore: any,
  basketsStore: any,
  wishlistStore: any,
  nuxtApp: NuxtApp,
  contentType: PageContentType,
  page: FirstLoadPage,
) {
  const { locale } = page
  const config = nuxtApp.$config
  const requestOrigin = resolveRequestOrigin()
  const organizationId = asString(config.public.SFCC_ORGANIZATION_ID)
  const sfccOriginFallback = asString(config.public.SFCC_ORIGIN)
  const countryCode = localeConfig[locale]?.country

  const { value: cartValue, products: cartProducts } = buildCartList(
    basketsStore.baskets?.[0],
    wishlistStore.wishlist,
    locale,
  )

  const { products: wishlistProducts } = buildWishList(wishlistStore.wishlist)

  const payload: DataLayerOptional = {
    route: {
      title: page.title,
      currentURI: page.fullPath,
      currentPath: page.path,
    },
    content: {
      type: contentType, // e.g. page template: home,article,plp,pdp,etc
      // version: deploymentVersion, // e.g. website deployment version
      region: countryCode, // AU / CA / NZ
      environment: getEnvironment(
        resolveLocalDevSfccOrigin(requestOrigin, organizationId, { origin: sfccOriginFallback }) || requestOrigin,
      ), // prod / stage
    },
  }

  const guestUserPayload: UserDataLayer = {
    id: userStore.guestUser?.customerId || '',
    loggedIn: false,
    brazeDeviceId: nuxtApp.$braze.sdk.getDeviceId() ?? '',
  }
  const cid = nuxtApp.$braze.sdk.getUser()?.getUserId()
  if (cid) {
    guestUserPayload.cid = cid
  }

  payload.user = userStore.user?.customerId ? buildUser(userStore.user) : guestUserPayload

  if (cartProducts.length) {
    payload.cart = {
      value: cartValue,
      products: cartProducts,
    }
  }

  if (wishlistStore.count) {
    payload.wishlist = {
      products: wishlistProducts,
    }
  }

  pushEventToDataLayer('custom.content.view', payload, locale)
}

export const onMountedFirstLoadHandler = () => {
  const nuxtApp = useNuxtApp()
  const userStore = useUserStore()
  const basketsStore = useBasketsStore()
  const wishlistStore = useWishlistStore()
  const contentType = usePageContentType().contentType.value
  // Read now, from the page's own route: the shopper can navigate on before the stores settle, and on a
  // change of page component Nuxt's global route still points at the previous page until it finishes.
  const { fullPath, path } = useRoute()
  const locale = nuxtApp.$i18n.locale.value
  const releasePageViewHold = expectPageView(path)
  // The page's head entries are pushed by now, but unhead writes <title> to the DOM asynchronously after
  // page:finish, so resolve the title from the head rather than the document.
  const page = injectHead()
    .resolveTags()
    .then((tags): FirstLoadPage => {
      const title = tags.find((tag) => tag.tag === 'title')?.textContent ?? document.title
      return { title, fullPath, path, locale }
    })

  // The page can mount before the layout's basket/wishlist load; the event reports both.
  Promise.all([page, whenLayoutStoresSettled()])
    .then(([page]) => {
      try {
        firstLoadEvent(userStore, basketsStore, wishlistStore, nuxtApp, contentType, page)
      } catch (e) {
        console.error('layout analytics error', e)
      }
    })
    .finally(releasePageViewHold)
}

const contentTypeToPageContentTypeMap = new Map<string, PageContentType>([
  ['productListingPage', 'plp'],
  ['blogArticle', 'article'],
  ['connectedContentPillar', 'blog landing'],
])

export const mapSlugToPageContentType = (slug: string): PageContentType => {
  if (slug === '/') {
    return 'home'
  }
  if (slug === '/connected') {
    return 'blog landing'
  }
  if (slug === '/customer-service/contact-us') {
    return 'contact us'
  }
  return 'unknown'
}

// Every Contentful contentPage is a content-page unless its slug has a dedicated type (home, connected, contact us).
export const mapContentfulContentTypeToPageContentType = (contentType: string, slug: string): PageContentType => {
  const mapped = contentTypeToPageContentTypeMap.get(contentType)
  if (mapped) return mapped
  const fromSlug = mapSlugToPageContentType(slug)
  if (fromSlug !== 'unknown') return fromSlug
  return contentType === 'contentPage' ? 'content-page' : 'unknown'
}
